// src/Components/Admin/MarkerIcons.js

import React, { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';

import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';

// Import Hook
import { useMarkerIconsManage } from '../../Hooks/useMarkerIconsManage';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

// Import Custom Icons
import {
  MenuAddNew,
  MenuSave,
  MenuSaveGrey,
  MenuDelete,
  MenuDeleteGrey,
  MenuIconCancel,
  MenuIconCancelGrey,
} from '../../assets/icons/CustomIcons';
// AMENDED: Removed MenuUploadGrey as it is no longer needed
import { MenuUpload } from '../../assets/icons/CustomIconsAdd2';

// --- STYLING CONSTANTS ---
const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';
const SKY_BLUE = '#00B0FF'; // Added for the spinning progress bar

// --- HELPER COMPONENT: Editable Cell (Buffered) ---
const EditableCell = ({
  value,
  isEditing,
  onChange,
  isEditModeActive,
  placeholder,
  onFocus,
  onBlur,
  autoFocus,
  makeUppercase,
}) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || '');

  // Keep local value in sync with external value ONLY when we aren't actively focused on it
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setLocalValue(value || '');
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const insertAtCursor = (textToInsert) => {
    const input = inputRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const val = input.value;
    const newVal = val.slice(0, start) + textToInsert + val.slice(end);
    setLocalValue(newVal);
    onChange(newVal); // IMMEDIATELY UPDATE PARENT DIRTY STATE
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = inputRef.current.selectionEnd =
          start + textToInsert.length;
      }
    }, 0);
  };

  const handleChange = (e) => {
    setLocalValue(e.target.value);
    onChange(e.target.value); // IMMEDIATELY UPDATE PARENT DIRTY STATE
  };

  const handleKeyDown = (e) => {
    // Pressing Enter commits the edit by forcing a blur
    if (e.key === 'Enter') {
      inputRef.current.blur();
      return;
    }
    if (makeUppercase) {
      // Intercept single character keys to override casing
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const keyLower = e.key.toLowerCase();
        const keyUpper = e.key.toUpperCase();
        // Check if the key is a letter
        if (keyLower !== keyUpper) {
          e.preventDefault();
          // Reverse the shift key logic for capitalization
          const char = e.shiftKey ? keyLower : keyUpper;
          insertAtCursor(char);
        }
      }
    }
  };

  const handlePaste = (e) => {
    if (makeUppercase) {
      e.preventDefault();
      const paste = (e.clipboardData || window.clipboardData)
        .getData('text')
        .toUpperCase();
      insertAtCursor(paste);
    }
  };

  const handleBlur = () => {
    // Commit changes to the parent hook when the cell loses focus
    if (localValue !== (value || '')) {
      onChange(localValue);
    }
    if (onBlur) onBlur(); // Fire blur to clear active focus lock
  };

  if (isEditing) {
    return (
      <TextField
        inputRef={inputRef}
        value={localValue}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onClick={(e) => e.stopPropagation()}
        onFocus={onFocus}
        variant='standard'
        size='small'
        fullWidth
        InputProps={{
          disableUnderline: true,
          style: { fontSize: '0.8rem', color: DARK_BLUE, fontWeight: 'normal' },
        }}
        sx={{
          backgroundColor: LIGHT_BLUE_BG,
          px: 1,
          borderRadius: 1,
          border: `1px solid ${ORANGE_COLOR}`,
          boxSizing: 'border-box',
          '& .MuiInputBase-root': {
            height: '100%',
            fontSize: '0.8rem',
            color: DARK_BLUE,
          },
          '& .MuiInputBase-input': {
            color: DARK_BLUE,
            fontWeight: 'normal',
          },
          '&:hover': { backgroundColor: LIGHT_BLUE_BG },
          '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
        }}
      />
    );
  }
  return (
    <Typography
      variant='body2'
      sx={{
        color: isEditModeActive ? 'text.primary' : 'text.secondary',
        fontWeight: isEditModeActive ? 500 : 'normal',
        minHeight: '20px',
      }}>
      {value}
    </Typography>
  );
};

export default function MarkerIcons({ setIsDirty }) {
  const { state, flags, alerts, usage, actions } = useMarkerIconsManage();

  const [makeUppercase, setMakeUppercase] = useState(true);

  // --- FETCH TEXT FORMATTING PREFERENCE ---
  useEffect(() => {
    const unsubFmt = onSnapshot(doc(db, 'settings', 'TextFormatting'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setMakeUppercase(data.makeUppercase !== undefined ? !!data.makeUppercase : true);
      }
    });
    return () => unsubFmt();
  }, []);

  // --- SYNC DIRTY STATE WITH PARENT ---
  useEffect(() => {
    if (setIsDirty) {
      setIsDirty(flags.isDirty);
    }
  }, [flags.isDirty, setIsDirty]);

  // --- SCROLL REF ---
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (state.newRows.length > 0 && tableContainerRef.current) {
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [state.newRows.length]);

  // --- STYLING ---
  const ICON_SIZE = 64;

  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
      {/* PROCESSING OVERLAY */}
      <Backdrop
        sx={{
          position: 'absolute',
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          zIndex: 100002, // AMENDED: Raised to sit above Admin Panel
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
        open={!!state.isProcessing}>
        <CircularProgress size={60} thickness={4} sx={{ color: SKY_BLUE }} />
      </Backdrop>

      <input
        type='file'
        ref={state.fileInputRef}
        style={{ display: 'none' }}
        accept='image/*'
        onChange={actions.handleFileChange}
      />

      {/* --- HEADER --- */}
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          paddingLeft: '5px',
          paddingRight: '5px',
          mb: 2,
        }}>
        {/* ADD NEW */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => actions.handleHeaderAction('ADD_NEW')}
            disabled={!!state.isProcessing} // Disabled while processing
          >
            <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        {/* SAVE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => flags.canSave && actions.handleHeaderAction('SAVE')}
            disabled={!flags.canSave || !!state.isProcessing}>
            {flags.canSave ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* DELETE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => flags.canDelete && actions.handleHeaderAction('DELETE')}
            disabled={!flags.canDelete || !!state.isProcessing}>
            {flags.canDelete ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* CLOSE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => flags.canClose && actions.handleHeaderAction('CLOSE')}
            disabled={!flags.canClose || !!state.isProcessing}>
            {flags.canClose ? (
              <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuIconCancelGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* --- TABLE --- */}
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        elevation={0}
        sx={{
          flexGrow: 1,
          width: '100%',
          boxSizing: 'border-box',
          // STYLE: Top Border Only (Orange)
          borderTop: '2px solid #ff9800',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
        }}>
        {state.loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}>
            <CircularProgress size={60} thickness={4} sx={{ color: SKY_BLUE }} />
          </Box>
        ) : (
          <Table
            stickyHeader
            size='small'
            aria-label='marker icons table'
            sx={{ width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  height: '36px', // Compact height
                  '& th': {
                    backgroundColor: '#fff3e0',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ff9800',
                    py: '4px', // Compressed padding
                  },
                }}>
                <TableCell padding='checkbox' sx={{ width: '50px' }} />
                <TableCell sx={{ width: '30%' }}>Icon Text</TableCell>
                <TableCell align='center' sx={{ width: '10%' }}>
                  Default
                </TableCell>
                <TableCell align='center' sx={{ width: '10%' }}>
                  Icon
                </TableCell>
                <TableCell sx={{ width: '20%' }}>Initial Category</TableCell>
                <TableCell align='center' sx={{ width: '10%' }}>
                  Archived
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.displayRows.map((row) => {
                const isSelected = state.selectedIds.includes(row.id);
                const isEditModeActive = true;
                const isRowEditable = true;

                return (
                  <TableRow
                    key={row.id}
                    hover={true}
                    selected={isSelected}
                    onClick={() => actions.handleCheckboxChange(row.id)}
                    sx={{
                      cursor: 'pointer',
                      height: '32px', // Compact row height
                      '& td': { py: '4px' }, // Compressed cell padding
                      '&:last-child td, &:last-child th': { border: 0 },
                      opacity: 1,
                      '&.Mui-selected': {
                        backgroundColor: 'transparent !important',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                      },
                    }}>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={isSelected}
                        sx={{ pointerEvents: 'none', p: 0.5 }} // Reduced checkbox margin
                      />
                    </TableCell>

                    <TableCell>
                      <EditableCell
                        value={row.name}
                        autoFocus={state.focusedRowId === row.id}
                        placeholder='Enter Icon Text'
                        isEditing={isRowEditable}
                        isEditModeActive={isEditModeActive}
                        makeUppercase={makeUppercase}
                        onFocus={() => actions.setFocusedRowId(row.id)}
                        onBlur={() => actions.setFocusedRowId(null)}
                        onChange={(val) => actions.handleFieldChange(row.id, 'name', val)}
                      />
                    </TableCell>

                    <TableCell align='center' onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size='small'
                        checked={!!row.isDefault}
                        disabled={false}
                        onChange={() => actions.handleSetDefault(row.id)}
                        sx={{ p: 0.5 }} // Reduced checkbox margin
                      />
                    </TableCell>

                    <TableCell
                      align='center'
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.handleIconCellClick(row.id);
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                      }}>
                      {row.icon ? (
                        <Tooltip title={'Click to Change Icon'}>
                          <Box
                            component='img'
                            src={row.icon}
                            alt={row.name}
                            sx={{ width: 24, height: 24, objectFit: 'contain' }} // Slightly smaller icon to fit 32px row
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title='Click to Upload Icon'>
                          <Box sx={{ display: 'inline-flex' }}>
                            <MenuUpload
                              sx={{ fontSize: 24 }} // Slightly smaller icon
                              iconScale={2}
                              textSize={'8px'}
                              textY={70}
                              iconX={18}
                              iconY={18}
                              label='Upload'
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <FormControl fullWidth size='small' variant='standard'>
                        <Select
                          value={row.categoryId || ''}
                          onChange={(e) =>
                            actions.handleFieldChange(
                              row.id,
                              'categoryId',
                              e.target.value,
                            )
                          }
                          disableUnderline
                          displayEmpty
                          MenuProps={{
                            style: { zIndex: 100020 }, // AMENDED: Raised above Admin Panel
                          }}
                          sx={{
                            fontSize: '0.8rem',
                            backgroundColor: LIGHT_BLUE_BG,
                            border: `1px solid ${ORANGE_COLOR}`,
                            borderRadius: 1,
                            color: DARK_BLUE,
                            fontWeight: 'normal',
                            '& .MuiSelect-select': {
                              paddingTop: '4px',
                              paddingBottom: '4px',
                              paddingLeft: '8px',
                              borderRadius: 1,
                              '&:focus': { backgroundColor: LIGHT_BLUE_BG },
                            },
                            '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                            '& .MuiSvgIcon-root': { color: '#000' },
                          }}>
                          <MenuItem value=''>
                            <em style={{ color: '#aaa' }}>None</em>
                          </MenuItem>
                          {state.categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    <TableCell align='center'>
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}>
                        <Checkbox
                          size='small'
                          checked={!!row.isArchived}
                          onChange={(e) =>
                            actions.handleFieldChange(
                              row.id,
                              'isArchived',
                              e.target.checked,
                            )
                          }
                          sx={{ p: 0.5 }} // Reduced checkbox margin
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* --- DIALOGS AMENDED: Z-Index 100005 --- */}
      <ConfirmDialog
        open={alerts.showUnsaved}
        onClose={() => alerts.setShowUnsaved(false)}
        onConfirm={actions.handleCloseConfirmed}
        message='Do you want to discard your changes?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />

      <ConfirmDialog
        open={alerts.showDelete}
        onClose={() => alerts.setShowDelete(false)}
        onConfirm={actions.handleConfirmDelete}
        message='This action will permanently delete the selected icons. Are you sure?'
        severity='error'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />

      <ConfirmDialog
        open={alerts.showDefaultDelete}
        onClose={() => alerts.setShowDefaultDelete(false)}
        message='You cannot delete the default icon'
        severity='warning'
        autoClose={2000}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />

      {/* AMENDED: Default Marker Archive Alert */}
      <ConfirmDialog
        open={alerts.showArchiveDefault}
        onClose={() => alerts.setShowArchiveDefault(false)}
        message='You cannot archive the default icon'
        severity='warning'
        autoClose={2000}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />

      <ConfirmDialog
        open={alerts.showUsageBlock}
        onClose={() => alerts.setShowUsageBlock(false)}
        onConfirm={() => alerts.setShowUsageBlock(false)}
        message={`You cannot delete this icon because it is currently used by ${usage.count} markers. Archive it instead`}
        severity='warning'
        confirmText='OK'
        cancelText={null}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />
    </Box>
  );
}
